// Marketing · Por aprobar — bandeja única de publicaciones y reels que preparó el agente o el
// equipo. Lo aprobado es exactamente lo que se publica (huella del contenido en el backend).
// Solo una persona admin decide; el backend responde 403 a llaves de servicio y al agente.
// Marketing Studio: /root/docs/marketing/marketing-studio-diseno.md §5 y §7.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardCheck, MessageSquareText, RefreshCw, XCircle } from 'lucide-react';

import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '@/modules/orion-shell/ui';

import { MarketingNoDisponible } from '../api';
import {
  decidir,
  fetchAjustes,
  fetchAprobaciones,
  type Ajustes,
  type Aprobacion,
  type Decision,
  type EstadoAprobacion,
} from '../studio';

type Notificar = (n: { type: string; title: string; message?: string }) => void;

const FILTROS: { key: EstadoAprobacion | 'todas'; label: string }[] = [
  { key: 'pending', label: 'Por aprobar' },
  { key: 'changes_requested', label: 'Cambios pedidos' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
  { key: 'todas', label: 'Todas' },
];

const ESTADO: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Por aprobar', cls: 'bg-primary/10 text-primary' },
  approved: { label: 'Aprobada', cls: 'bg-primary/15 text-primary' },
  changes_requested: { label: 'Cambios pedidos', cls: 'bg-destructive/10 text-destructive' },
  rejected: { label: 'Rechazada', cls: 'bg-muted text-muted-foreground' },
  superseded: { label: 'Reemplazada', cls: 'bg-muted text-muted-foreground' },
  expired: { label: 'Vencida', cls: 'bg-muted text-muted-foreground' },
};

const TIPO: Record<string, string> = { reel: 'Reel', feed: 'Publicación', story: 'Historia' };
const RED: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook', both: 'Facebook + Instagram' };

// Hora del navegador, igual que el campo «Publicar el» (datetime-local), para que ambas coincidan.
const fmt = new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const fecha = (iso: string | null) => (iso ? fmt.format(new Date(iso)) : '—');

function pad(n: number) {
  return String(n).padStart(2, '0');
}
/** ISO → valor de <input type="datetime-local"> en la hora local del navegador. */
function aInputLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Chip({ cls, children }: { cls: string; children: string }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>{children}</span>;
}

function Medios({ a }: { a: Aprobacion }) {
  const medios = a.post?.media ?? [];
  if (!medios.length) return <p className="text-xs text-muted-foreground">Sin imagen ni video.</p>;
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {medios.map((m) =>
        m.kind === 'video' ? (
          <video
            key={m.id}
            src={m.url}
            controls
            playsInline
            preload="metadata"
            className="max-h-80 w-auto max-w-full shrink-0 rounded-xl border border-border bg-muted"
          />
        ) : (
          <img
            key={m.id}
            src={m.url}
            alt="Imagen de la publicación"
            loading="lazy"
            className="max-h-80 w-auto max-w-full shrink-0 rounded-xl border border-border bg-muted object-contain"
          />
        ),
      )}
    </div>
  );
}

function Pieza({
  a,
  ocupado,
  onDecidir,
}: {
  a: Aprobacion;
  ocupado: boolean;
  onDecidir: (a: Aprobacion, d: Decision, body: { comment?: string; scheduled_at?: string }) => Promise<boolean>;
}) {
  const [modo, setModo] = useState<'nada' | 'cambios' | 'rechazo'>('nada');
  const [comentario, setComentario] = useState('');
  const [cuando, setCuando] = useState(aInputLocal(a.post?.scheduled_at ?? null));
  const pendiente = a.status === 'pending';
  const p = a.post;
  const est = ESTADO[a.status] ?? { label: a.status, cls: 'bg-muted text-muted-foreground' };
  const titulo = p ? `${TIPO[p.post_type] ?? p.post_type} · ${RED[p.platform] ?? p.platform}` : 'Pieza no disponible';

  const enviar = async (d: Decision) => {
    const body: { comment?: string; scheduled_at?: string } = {};
    if (comentario.trim()) body.comment = comentario.trim();
    if (d === 'aprobar' && cuando) body.scheduled_at = new Date(cuando).toISOString();
    const ok = await onDecidir(a, d, body);
    if (ok) {
      setModo('nada');
      setComentario('');
    }
  };

  return (
    <Card
      title={titulo}
      action={<Chip cls={est.cls}>{est.label}</Chip>}
      bodyClassName="space-y-3"
    >
      <p className="text-xs text-muted-foreground">
        Pedida por {a.requested_by || 'sin registro'} · {fecha(a.created_at)}
      </p>

      {pendiente && a.stale && (
        <p className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden />
          La pieza cambió después de pedir esta aprobación. Revisa la versión nueva antes de decidir.
        </p>
      )}

      {p ? (
        <>
          <Medios a={a} />
          {p.caption ? (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">{p.caption}</p>
          ) : (
            <p className="text-xs text-muted-foreground">Sin texto.</p>
          )}
          <p className="text-xs text-muted-foreground">Programada para {fecha(p.scheduled_at)}</p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">La publicación ya no existe.</p>
      )}

      {!pendiente && (a.decided_by || a.comment) && (
        <div className="rounded-xl bg-muted px-3 py-2 text-xs text-foreground">
          {a.decided_by && (
            <p>
              Decidió {a.decided_by} · {fecha(a.decided_at)}
            </p>
          )}
          {a.comment && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">«{a.comment}»</p>}
        </div>
      )}

      {pendiente && p && !a.stale && (
        <div className="space-y-3 border-t border-border pt-3">
          {modo === 'nada' && (
            <>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-2">
                Publicar el
                <input
                  type="datetime-local"
                  value={cuando}
                  onChange={(e) => setCuando(e.target.value)}
                  className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                />
                <span>(si la fecha ya pasó, sale en un minuto)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <Button variant="brand" disabled={ocupado} onClick={() => enviar('aprobar')}>
                  <CheckCircle2 size={15} aria-hidden /> Aprobar
                </Button>
                <Button disabled={ocupado} onClick={() => setModo('cambios')}>
                  <MessageSquareText size={15} aria-hidden /> Pedir cambios
                </Button>
                <Button variant="danger" disabled={ocupado} onClick={() => setModo('rechazo')}>
                  <XCircle size={15} aria-hidden /> Rechazar
                </Button>
              </div>
            </>
          )}
          {modo !== 'nada' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-foreground" htmlFor={`com-${a.id}`}>
                {modo === 'cambios' ? '¿Qué hay que cambiar? El agente lo rehace con tu comentario.' : 'Motivo (opcional)'}
              </label>
              <textarea
                id={`com-${a.id}`}
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder={modo === 'cambios' ? 'Ej.: cambia el cierre por «Te esperamos el sábado» y usa la foto del puesto' : ''}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={modo === 'cambios' ? 'primary' : 'danger'}
                  disabled={ocupado || (modo === 'cambios' && !comentario.trim())}
                  onClick={() => enviar(modo === 'cambios' ? 'pedir-cambios' : 'rechazar')}
                >
                  {modo === 'cambios' ? 'Enviar cambios' : 'Rechazar'}
                </Button>
                <Button variant="ghost" disabled={ocupado} onClick={() => setModo('nada')}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function PorAprobarView({ addNotification }: { addNotification?: Notificar }) {
  const [filtro, setFiltro] = useState<EstadoAprobacion | 'todas'>('pending');
  const [items, setItems] = useState<Aprobacion[]>([]);
  const [ajustes, setAjustes] = useState<Ajustes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState<number | 'lote' | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lista, aj] = await Promise.all([
        fetchAprobaciones(filtro === 'todas' ? undefined : filtro),
        fetchAjustes().catch(() => null),
      ]);
      setItems(lista);
      setAjustes(aj);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aprobables = useMemo(() => items.filter((a) => a.status === 'pending' && !a.stale && a.post), [items]);

  const onDecidir = useCallback(
    async (a: Aprobacion, d: Decision, body: { comment?: string; scheduled_at?: string }) => {
      setOcupado(a.id);
      try {
        const nueva = await decidir(a.id, d, body);
        setItems((xs) =>
          filtro === 'todas' || nueva.status === filtro ? xs.map((x) => (x.id === a.id ? nueva : x)) : xs.filter((x) => x.id !== a.id),
        );
        const titulo = { aprobar: 'Aprobada y programada', 'pedir-cambios': 'Cambios enviados', rechazar: 'Rechazada' }[d];
        addNotification?.({ type: 'success', title: titulo });
        return true;
      } catch (e) {
        addNotification?.({ type: 'error', title: 'No se pudo guardar la decisión', message: e instanceof Error ? e.message : undefined });
        if (e instanceof Error && /cambió|ya no está pendiente|ya no existe/.test(e.message)) cargar();
        return false;
      } finally {
        setOcupado(null);
      }
    },
    [addNotification, cargar, filtro],
  );

  const aprobarTodas = async () => {
    if (!aprobables.length) return;
    if (!window.confirm(`¿Aprobar ${aprobables.length} pieza(s)? Se publicarán en la fecha que tiene cada una.`)) return;
    setOcupado('lote');
    let ok = 0;
    const errores: string[] = [];
    for (const a of aprobables) {
      try {
        await decidir(a.id, 'aprobar');
        ok += 1;
      } catch (e) {
        errores.push(e instanceof Error ? e.message : `#${a.id}`);
      }
    }
    setOcupado(null);
    addNotification?.({
      type: errores.length ? 'warning' : 'success',
      title: `${ok} aprobada(s)${errores.length ? `, ${errores.length} con error` : ''}`,
      message: errores[0],
    });
    cargar();
  };

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <PageHeader
        title="Por aprobar"
        subtitle="Publicaciones y reels que preparó el agente o tu equipo. Lo que apruebas es exactamente lo que se publica."
        actions={
          <>
            {filtro === 'pending' && aprobables.length > 1 && (
              <Button variant="brand" disabled={ocupado !== null} onClick={aprobarTodas}>
                <ClipboardCheck size={15} aria-hidden /> Aprobar todas ({aprobables.length})
              </Button>
            )}
            <Button onClick={cargar} disabled={loading} aria-label="Actualizar">
              <RefreshCw size={15} aria-hidden />
            </Button>
          </>
        }
      />

      {ajustes && !ajustes.enabled && (
        <div className="mb-4 rounded-2xl border border-border bg-card px-4 py-3 text-sm text-foreground">
          <p className="font-medium">La aprobación todavía no está activada.</p>
          <p className="mt-1 text-muted-foreground">
            Hoy las publicaciones y los reels salen solos en su fecha. Actívala en Marketing → Ajustes para que nada se
            publique sin el visto bueno de un admin.
          </p>
        </div>
      )}

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Filtrar por estado">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filtro === f.key}
            onClick={() => setFiltro(f.key)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              filtro === f.key ? 'bg-primary text-primary-foreground' : 'border border-border bg-card text-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : error instanceof MarketingNoDisponible ? (
        <EmptyState icon={<ClipboardCheck size={18} />} title="La bandeja de aprobación todavía no está disponible">
          El servidor aún no tiene Marketing Studio. En cuanto se publique, aquí verás lo que falta aprobar.
        </EmptyState>
      ) : error ? (
        <div className="space-y-3">
          <ErrorNote>{error instanceof Error ? error.message : 'No se pudo cargar la bandeja.'}</ErrorNote>
          <Button onClick={cargar}>
            <RefreshCw size={14} aria-hidden /> Reintentar
          </Button>
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={<CheckCircle2 size={18} />} title={filtro === 'pending' ? 'Nada por aprobar' : 'Sin piezas en este estado'}>
          {filtro === 'pending'
            ? 'Cuando el agente o tu equipo manden una publicación o un reel a aprobación, aparecerá aquí.'
            : 'Prueba con otro filtro.'}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {items.map((a) => (
            <Pieza key={a.id} a={a} ocupado={ocupado !== null} onDecidir={onDecidir} />
          ))}
        </div>
      )}
    </div>
  );
}
